'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import cx from 'classnames';

import { Product } from '../Products';

import { Image } from '@/components/Default/Image';

interface CardProps extends Product {
    rest?: any;
    animate?: any;
}

const Card = ({ title, description, src, thumbnail, href, rest, available = true, animate = {} }: CardProps) => {
    const [isHover, setIsHover] = useState<boolean>(false);

    const variants = {
        rest: {
            transition: { duration: 0.7 },
            ...rest
        },
        hover: {
            scale: 1.05,
            y: -6,
            ...animate
        },
        press: { scale: available ? 0.99 : 1 }
    };

    const Button = (
        <motion.button
            className={cx('flex flex-col overflow-hidden w-full h-full appearance-none border bg-white text-neutral-700 rounded-2xl sm:rounded-[36px] outline-none m-0 px-0 relative text-center items-center', {
                'hover:border-none hover:shadow-pillow cursor-pointer': available,
                'cursor-default': ! available
            })}
            initial={false}
            animate={[
                isHover && available ? 'hover' : 'rest'
            ]}
            whileTap='press'
            variants={variants}
            onHoverStart={() => setIsHover(true)}
            onHoverEnd={() => setIsHover(false)}
        >
            <Image src={thumbnail} alt='' width={1920} height={1080} className='h-full' />
            <label className={cx('absolute right-4 top-4 bg-transparent border rounded-full text-xs font-medium empty:hidden', {
                'border-emerald-500': available === true,
                'border-gray-700 text-gray-700 py-0.5 sm:py-1 px-1.5 sm:px-2.5': available === false
            })}>
                { available
                    ? (
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256' className='w-5 sm:w-8 h-5 sm:h-8 fill-emerald-500'>
                            <path d='M128,230.4a102.4,102.4,0,1,0,0-204.8h0a102.4,102.4,0,0,0,0,204.8' />
                            <path d='M177.37,104.85a9.6,9.6,0,1,0-15.54-11.29l-44.58,61.31L93.18,130.8a9.6,9.6,0,1,0-13.79,13.35l.23.23,32,32a9.59,9.59,0,0,0,13.57,0,8.89,8.89,0,0,0,1-1.15Z' fill='white' />
                        </svg>
                    )
                    : (
                        <div className='flex gap-x-1 items-center'>
                            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='w-3 h-3'>
                                <path fillRule='evenodd' d='M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z' clipRule='evenodd' />
                            </svg>
                            <span>Wordt verwacht</span>
                        </div>
                    )
                }
            </label>
            <div className='relative w-full sm:w-96 px-4 pt-16 pb-8 text-center'>
                <div className='bg-black inline-block aspect-square rounded-xl absolute top-0 left-4 sm:left-8 -translate-y-1/2'>
                    <Image src={src} alt='' width={256} height={256} className={cx('w-12 sm:w-24 h-auto mx-auto', {
                        'grayscale opacity-25': ! available
                    })} draggable={false} loading='eager' />
                </div>
                <h1 className={cx('text-lg sm:text-2xl tracking-tight font-bold', {
                    'text-neutral-400': ! available
                })}>
                    {title}
                </h1>

                <p className={cx('text-sm sm:text-base min-h-[5rem]', {
                    'text-neutral-400': ! available
                })}>
                    {description}
                </p>
            </div>
        </motion.button>
    );

    if (available) {
        return (
            <Link href={href}>
                {Button}
            </Link>
        );
    }

    return Button;
};

export { Card };